import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BaseStoreFacade } from '@city/data-access';
import { CookieService } from '@core/feature';
import { SmartBannerFacade } from '@main/smart-banner/data-access';
import { DeviceService } from '@shared/services';
import { WindowRef } from '@spartacus/core';

const SMART_BANNER_LAST_CLOSED_KEY = 'smartBannerLastClosed';
const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class SmartBannerService {
  private readonly winRef = inject(WindowRef);
  private readonly deviceService = inject(DeviceService);
  private readonly cookieService = inject(CookieService);
  private readonly smartBannerFacade = inject(SmartBannerFacade);
  private readonly baseStoreFacade = inject(BaseStoreFacade);
  private readonly destroyRef = inject(DestroyRef);

  readonly smartBanner = toSignal(this.smartBannerFacade.getSmartBanner(), { initialValue: null });
  readonly isLoaded = toSignal(this.smartBannerFacade.getSmartBannerSuccess(), { initialValue: false });

  private readonly bannerState = signal<boolean>(false);

  readonly bannerTitle = computed(() => {
    const title = this.smartBanner()?.title?.trim();
    return title || 'Удобнее в приложении';
  });

  readonly visibleItems = computed(() => {
    return this.smartBanner()?.description?.filter((item) => item.field?.trim()?.length > 0) || [];
  });

  readonly showSmartBanner = computed(() => {
    return (
      this.bannerState() &&
      !this.deviceService.isSdvorApp() &&
      !this.cookieService.isAutomationTest() &&
      this.isLoaded()
    );
  });

  init(): void {
    this.bannerState.set(this.shouldShowBanner());

    if (this.bannerState()) {
      this.baseStoreFacade
        .getActiveCode()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((cityCode) => {
          this.smartBannerFacade.loadSmartBanner(cityCode);
        });
    }
  }

  closeBanner(): void {
    const nowUTC = new Date().toISOString();
    this.winRef.localStorage?.setItem(SMART_BANNER_LAST_CLOSED_KEY, nowUTC);
    this.bannerState.set(false);
  }

  private shouldShowBanner(): boolean {
    const lastClosed = this.winRef.localStorage?.getItem(SMART_BANNER_LAST_CLOSED_KEY);

    if (!lastClosed) {
      return true;
    }

    try {
      const lastClosedDate = new Date(lastClosed);
      const now = new Date();
      return now.getTime() - lastClosedDate.getTime() >= TWO_WEEKS_MS;
    } catch {
      return true;
    }
  }
}
